import { IconCalendar, IconClock, IconHourglass } from '@/theme/icons';
import {
  formatStringToDate,
  utcToLocalDate,
  type RecurringOccurrenceTooltipInfo,
} from '@/utils/calendar';
import { getDateFnsLocale } from '@/utils/dateLocale';
import { convertToMinutes } from '@/utils/events';
import { capitalizeFirstLetter, humanDuration } from '@/utils/index';
import rem from '@/utils/rem';
import { Flex, Icon, Text, Tooltip, VStack } from '@chakra-ui/react';
import { addDays, format, isSameDay } from 'date-fns';
import type { Locale } from 'date-fns';
import { useTranslation } from 'react-i18next';

function isScheduled(scheduledDate: string | null, scheduledTime: string | null) {
  return scheduledDate !== null && scheduledTime !== null;
}

function formatWeeklyOccurrenceDatePhrase(
  d: Date,
  now: Date,
  dateLocale: Locale,
  noScheduledTime: boolean,
  t: (key: string) => string,
): string {
  const today = isSameDay(d, now);
  const tomorrow = isSameDay(d, addDays(now, 1));
  const dayWord = today ? t('tooltips.today') : tomorrow ? t('tooltips.tomorrow') : format(d, 'MMMM d', { locale: dateLocale });

  if (noScheduledTime) {
    return capitalizeFirstLetter(dayWord);
  }

  const timePart = format(d, 'h:mma', { locale: dateLocale }).toLowerCase();
  return today || tomorrow ? `${timePart}, ${dayWord}` : capitalizeFirstLetter(format(d, "MMMM d',' yyyy '/' h:mma", { locale: dateLocale }));
}

function buildRecurringTooltipLabel(
  info: RecurringOccurrenceTooltipInfo,
  t: (key: string, opts?: Record<string, unknown>) => string,
  dateLocale: Locale,
  durationText: string,
  now: Date,
) {
  const lineFormat = info.lineFormat ?? 'default';
  let main: string;

  if (info.nextStart) {
    if (lineFormat === 'weeklyNext' || lineFormat === 'weeklyPlain') {
      const datePhrase = formatWeeklyOccurrenceDatePhrase(info.nextStart, now, dateLocale, info.noScheduledTime, t);
      if (lineFormat === 'weeklyPlain') {
        main = info.noScheduledTime
          ? t('tooltips.weeklyPlainPlanned', { date: datePhrase, duration: durationText })
          : t('tooltips.weeklyPlainScheduled', { date: datePhrase, duration: durationText });
      } else {
        main = info.noScheduledTime
          ? t('tooltips.weeklyNextPlanned', { date: datePhrase, duration: durationText })
          : t('tooltips.weeklyNextScheduled', { date: datePhrase, duration: durationText });
      }
    } else if (info.noScheduledTime) {
      const formattedDate = capitalizeFirstLetter(
        format(info.nextStart, "MMMM dd',' yyyy", { locale: dateLocale }),
      );
      main = t('tooltips.nextOccurrencePlanned', { date: formattedDate, duration: durationText });
    } else {
      const formattedDate = capitalizeFirstLetter(
        format(info.nextStart, "MMMM dd',' yyyy '/' h:mma", { locale: dateLocale }),
      );
      main = t('tooltips.nextOccurrenceScheduled', { date: formattedDate, duration: durationText });
    }
  } else {
    main = t('tooltips.noUpcomingOccurrence');
  }
  return main;
}

function buildLastCompletedTooltipLabel(
  lastStart: Date,
  noScheduledTime: boolean,
  t: (key: string, opts?: Record<string, unknown>) => string,
  dateLocale: Locale,
  durationText: string,
  now: Date,
  lineFormat: 'default' | 'weeklyLast' | 'weeklyPlain' = 'default',
) {
  if (lineFormat === 'weeklyPlain') {
    const datePhrase = formatWeeklyOccurrenceDatePhrase(lastStart, now, dateLocale, noScheduledTime, t);
    return noScheduledTime
      ? t('tooltips.weeklyPlainPlanned', { date: datePhrase, duration: durationText })
      : t('tooltips.weeklyPlainScheduled', { date: datePhrase, duration: durationText });
  }

  if (lineFormat === 'weeklyLast') {
    const datePhrase = formatWeeklyOccurrenceDatePhrase(lastStart, now, dateLocale, noScheduledTime, t);
    return noScheduledTime
      ? t('tooltips.weeklyLastPlanned', { date: datePhrase, duration: durationText })
      : t('tooltips.weeklyLastScheduled', { date: datePhrase, duration: durationText });
  }

  if (noScheduledTime) {
    const formattedDate = capitalizeFirstLetter(format(lastStart, "MMMM dd',' yyyy", { locale: dateLocale }));
    return t('tooltips.lastOccurrencePlanned', { date: formattedDate, duration: durationText });
  }
  const formattedDate = capitalizeFirstLetter(
    format(lastStart, "MMMM dd',' yyyy '/' h:mma", { locale: dateLocale }),
  );
  return t('tooltips.lastOccurrenceScheduled', { date: formattedDate, duration: durationText });
}

type ClockProps = {
  iconColor?: string;
  scheduledDate: string | null;
  scheduledTime: string | null;
  timeZone?: string;
  gmtOffset?: string;
  duration: string;
  recurringLastCompletedTooltip?: {
    lastStart: Date;
    noScheduledTime: boolean;
    lineFormat?: 'default' | 'weeklyLast' | 'weeklyPlain';
  } | null;
  recurringCompletedPeriodList?: { dates: Date[]; noScheduledTime: boolean } | null;
  recurringOccurrenceTooltip?: RecurringOccurrenceTooltipInfo | null;
};

function formatReflectionCompletedLine(
  d: Date,
  noScheduledTime: boolean,
  dateLocale: Locale,
  durationText: string,
): string {
  if (noScheduledTime) {
    return `${capitalizeFirstLetter(format(d, "MMMM d',' yyyy", { locale: dateLocale }))} / ${durationText}`;
  }
  const dateTime = capitalizeFirstLetter(format(d, "MMMM d',' yyyy '/' h:mma", { locale: dateLocale }).toLowerCase());
  return `${dateTime} / ${durationText}`;
}

function Clock({
  iconColor = 'white.500',
  scheduledDate,
  scheduledTime,
  duration,
  timeZone,
  gmtOffset,
  recurringLastCompletedTooltip,
  recurringCompletedPeriodList,
  recurringOccurrenceTooltip,
}: ClockProps) {
  const { t, i18n } = useTranslation('ActionRow');

  // Get the appropriate locale for date-fns based on current language
  const dateLocale = getDateFnsLocale(i18n.language);
  const durationText = humanDuration(convertToMinutes(duration) * 60);
  const now = new Date();

  if (recurringCompletedPeriodList && recurringCompletedPeriodList.dates.length > 0) {
    const { dates, noScheduledTime } = recurringCompletedPeriodList;
    const RecurringIcon = noScheduledTime ? IconCalendar : IconClock;

    return (
      <Tooltip
        aria-label={t('tooltips.completedOccurrencesHeading')}
        label={
          <VStack align="flex-start" spacing={1}>
            <Text as="span" fontWeight="semibold">
              {t('tooltips.completedOccurrencesHeading')}
            </Text>
            {dates.map((d, index) => (
              <Text key={`${d.getTime()}-${index}`} as="span" display="block">
                {`${index + 1}. ${formatReflectionCompletedLine(d, noScheduledTime, dateLocale, durationText)}`}
              </Text>
            ))}
          </VStack>
        }
        placement="top"
      >
        <Flex>
          <Icon as={RecurringIcon} boxSize={rem(14)} color={iconColor} />
        </Flex>
      </Tooltip>
    );
  }

  if (recurringLastCompletedTooltip) {
    const { lastStart, noScheduledTime, lineFormat } = recurringLastCompletedTooltip;
    const label = buildLastCompletedTooltipLabel(
      lastStart,
      noScheduledTime,
      t,
      dateLocale,
      durationText,
      now,
      lineFormat,
    );
    const RecurringIcon = noScheduledTime ? IconCalendar : IconClock;

    return (
      <Tooltip
        aria-label={label}
        label={
          <Text as="span" display="block" whiteSpace="pre-line">
            {label}
          </Text>
        }
        placement="top"
      >
        <Flex>
          <Icon as={RecurringIcon} boxSize={rem(14)} color={iconColor} />
        </Flex>
      </Tooltip>
    );
  }

  if (recurringOccurrenceTooltip) {
    const label = buildRecurringTooltipLabel(recurringOccurrenceTooltip, t, dateLocale, durationText, now);
    const RecurringIcon =
      recurringOccurrenceTooltip.nextStart && recurringOccurrenceTooltip.noScheduledTime ? IconCalendar : IconClock;

    return (
      <Tooltip
        aria-label={label}
        label={
          <Text as="span" display="block" whiteSpace="pre-line">
            {label}
          </Text>
        }
        placement="top"
      >
        <Flex>
          <Icon as={RecurringIcon} boxSize={rem(14)} color={iconColor} />
        </Flex>
      </Tooltip>
    );
  }

  if (isScheduled(scheduledDate, scheduledTime) && scheduledDate) {
    const formattedDate = capitalizeFirstLetter(
      format(utcToLocalDate(scheduledDate, scheduledTime, timeZone, gmtOffset), "MMMM dd',' yyyy '/' h:mma", {
        locale: dateLocale,
      }),
    );
    const label = t('tooltips.scheduled', { date: formattedDate, duration: durationText });

    return (
      <Tooltip aria-label={label} label={label} placement="top">
        <Flex>
          <Icon as={IconClock} boxSize={rem(14)} color={iconColor} />
        </Flex>
      </Tooltip>
    );
  }

  if (scheduledDate !== null) {
    const formattedDate = capitalizeFirstLetter(
      format(formatStringToDate(scheduledDate), "MMMM dd',' yyyy", { locale: dateLocale }),
    );
    const label = t('tooltips.planned', { date: formattedDate, duration: durationText });

    return (
      <Tooltip aria-label={label} label={label} placement="top">
        <Flex>
          <Icon as={IconCalendar} boxSize={rem(14)} color={iconColor} />
        </Flex>
      </Tooltip>
    );
  }

  const label = t('tooltips.duration', { duration: durationText });

  return (
    <Tooltip aria-label={label} label={label} placement="top">
      <Flex>
        <Icon as={IconHourglass} boxSize={rem(14)} color={iconColor} />
      </Flex>
    </Tooltip>
  );
}

export default Clock;
